import React, { useState } from 'react';
import { Card } from 'antd';
import { HolderOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

export const SortingWidget = ({ config }) => {
    const { items = [], categories = [] } = config;
    const [itemLocations, setItemLocations] = useState(Object.fromEntries(items.map(i => [i.id, null])));
    const [incorrectItems, setIncorrectItems] = useState(new Set());

    const handleDrop = (itemId, categoryId) => {
        // Prevent interaction if item is currently animating incorrect
        if (incorrectItems.has(itemId)) return;

        const item = items.find(i => i.id === itemId);

        // If dropping back to bank (categoryId === null)
        if (categoryId === null) {
            setItemLocations(prev => ({ ...prev, [itemId]: null }));
            return;
        }

        const isCorrect = item.correctCategoryId === categoryId;
        setItemLocations(prev => ({ ...prev, [itemId]: categoryId }));

        if (!isCorrect) {
            setIncorrectItems(prev => new Set(prev).add(itemId));
            setTimeout(() => {
                setItemLocations(prev => ({ ...prev, [itemId]: null }));
                setIncorrectItems(prev => {
                    const next = new Set(prev);
                    next.delete(itemId);
                    return next;
                });
            }, 600);
        }
    };

    const renderItem = (item) => {
        const isLocked = incorrectItems.has(item.id);
        const categoryId = itemLocations[item.id];
        const isPlaced = categoryId !== null;
        const isCorrect = isPlaced && item.correctCategoryId === categoryId;

        return (
            <div
                key={item.id}
                draggable={!isLocked}
                onDragStart={(e) => e.dataTransfer.setData('itemId', item.id)}
                className={`ch-sorting-item ${isLocked ? 'ch-shake' : ''}`}
                style={{ cursor: isLocked ? 'not-allowed' : 'grab' }}
            >
                <HolderOutlined className="ch-drag-handle" />
                <span className="ch-item-content">{item.content}</span>
                {isPlaced && (isCorrect ? <CheckCircleOutlined className="ch-correct-icon" /> : <CloseCircleOutlined className="ch-incorrect-icon" />)}
            </div>
        );
    };

    return (
        <div className="flexColumn mediumGap ch-width-100">
            {/* BANK (Drop Zone for resetting) */}
            <Card
                title="Items"
                className="ch-sorting-bank ch-card"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                    const id = e.dataTransfer.getData('itemId');
                    if (id) handleDrop(id, null);
                }}
            >
                {items.filter(item => itemLocations[item.id] === null).map(renderItem)}
                {items.filter(item => itemLocations[item.id] === null).length === 0 && <div className="ch-text-small text-muted">All items placed</div>}
            </Card>

            {/* CATEGORIES */}
            <div className="ch-sorting-categories flexRow mediumGap align-stretch">
                {categories.map(cat => (
                    <Card
                        key={cat.id}
                        title={cat.label}
                        className="ch-sorting-category flex1 flexColumn"
                        style={{ borderTop: `4px solid ${cat.color || '#1890ff'}` }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                            const id = e.dataTransfer.getData('itemId');
                            if (id) handleDrop(id, cat.id);
                        }}
                    >
                        {items.filter(item => itemLocations[item.id] === cat.id).map(renderItem)}
                        <div className="ch-drop-hint">Drop here</div>
                    </Card>
                ))}
            </div>
        </div>
    );
};